import type { JSX } from "solid-js";
import { createMemo, Show, untrack } from "solid-js";
import { isNil } from "lodash-es";
import { object } from "valibot";
import InputModal from "../../../components/modal/InputModal";
import { SingleSelectWithSearchInput } from "../../../components/menu/SingleSelectWithSearch";
import { useModalManager } from "../../../components/modal/ModalManager";
import { useTranslation } from "../../../state/i18n";
import ApiService from "../../../services/ApiService";
import UserService from "../../../services/UserService";
import GenericFailureModal from "../../GenericFailureModal";
import { toSelectOption } from "../../../utils/avatarUtil";
import { userReference } from "../../../utils/validation/idReferences";
import { getReassignableUsers } from "../../../utils/taskUtil";
import { getUsersWithFormAccess } from "../../../utils/permissionUtil";
import type { Task, TaskUser } from "../../../types/Task";
import type { SelectOption } from "../../../types/Menu";
import type { User } from "../../../state/user";
import type UserId from "../../../state/UserId";
import type { MoreAppError } from "../../../MoreAppError";

export const reassignTaskSchema = () =>
  object({
    from: userReference(),
    to: userReference(),
  });

type ReassignTaskModalProps = {
  task: Task;
};

const ReassignTaskModal = (props: ReassignTaskModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const transferTask = ApiService.transferTask();
  const customerId = untrack(() => props.task.customerId);
  const usersQuery = UserService.getUsers(customerId);
  const groupsQuery = UserService.getGroups(customerId);

  const reassignableUsers = createMemo(() => getReassignableUsers(props.task));
  const hasSingleReassignableUser = createMemo(() => reassignableUsers().length === 1);

  const showFailure = (error: MoreAppError): void =>
    replace({
      content: () => (
        <GenericFailureModal title={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_FAILURE_MODAL_TITLE)} error={error} />
      ),
    });

  const userData = createMemo(() =>
    !isNil(usersQuery.data) && !isNil(groupsQuery.data)
      ? { users: usersQuery.data, groups: groupsQuery.data }
      : undefined,
  );

  return (
    <InputModal
      variant="default"
      size="large"
      title={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_MODAL_TITLE)}
      titleIcon="reassign"
      confirmLabel={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_MODAL_CONFIRM)}
      cancelLabel={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_CANCEL)}
      onConfirm={({ from, to }) => {
        const request = { formId: props.task.formId, taskId: props.task.id, fromUserId: from, toUserId: to };
        return new Promise((resolve, reject) => {
          transferTask.mutate(request, {
            onSuccess: resolve,
            onError: (err) => {
              showFailure(err);
              reject(err);
            },
          });
        });
      }}
      schema={reassignTaskSchema()}
      initialValue={{
        from: untrack(() => (hasSingleReassignableUser() ? reassignableUsers()[0].userId : undefined)),
        to: undefined,
      }}
    >
      {({ value, updateValue, error }) => (
        <Show when={userData()}>
          {(data) => (
            <div class="flex flex-col gap-6">
              <SingleSelectWithSearchInput<UserId>
                label={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_MODAL_FROM_LABEL)}
                placeholder={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_MODAL_SELECT_USER_PLACEHOLDER)}
                value={value("from")}
                options={{
                  users: reassignableUsers()
                    .filter((taskUser) => !isSelected(taskUser.userId, value("to")))
                    .map((taskUser) => toTaskUserOption(taskUser, data().users)),
                }}
                onSelect={(userId) => updateValue("from", userId ?? undefined)}
                disabled={hasSingleReassignableUser()}
                width="matchTrigger"
                errorMessage={error("from")}
              />
              <SingleSelectWithSearchInput<UserId>
                label={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_MODAL_TO_LABEL)}
                placeholder={t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN_MODAL_SELECT_USER_PLACEHOLDER)}
                value={value("to")}
                options={{
                  users: getUsersWithFormAccess(data().users, data().groups, props.task.formId)
                    .filter((user) => !isSelected(user.id, value("from")))
                    .map(toUserOption),
                }}
                onSelect={(userId) => updateValue("to", userId ?? undefined)}
                width="matchTrigger"
                errorMessage={error("to")}
              />
            </div>
          )}
        </Show>
      )}
    </InputModal>
  );
};

const isSelected = (userId: UserId, selectedUserId: UserId | undefined): boolean =>
  !isNil(selectedUserId) && userId.equals(selectedUserId);

const toUserOption = (user: User): SelectOption<UserId> => {
  const option = toSelectOption(user);
  return { ...option, value: option.value.id };
};

// Recipients who were deleted in the meantime are no longer part of the customer's users
const toTaskUserOption = (taskUser: TaskUser, users: User[]): SelectOption<UserId> => {
  const user = users.find(({ id }) => id.equals(taskUser.userId));
  return isNil(user) ? { variant: "text", label: taskUser.username, value: taskUser.userId } : toUserOption(user);
};

export default ReassignTaskModal;
